import {
  ClockIcon,
  CloseIcon,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  IconButton,
  IconButtonColorEnum,
  IconButtonSizeEnum,
  IconSizeEnum,
  TIcon,
  Typography,
} from '@acceso/design-system';
import { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useCatalogueSelectProps } from '@/components/CatalogueListbox/use-catalogue-select-props';
import { FooterFormActions } from '@/components/FooterFormActions/FooterFormActions';
import { InputDate } from '@/components/FormFields/InputDate';
import { InputNumber } from '@/components/FormFields/InputNumber';
import { RowContainer } from '@/components/FormFields/RowContainer';
import { SelectForm } from '@/components/FormFields/SelectForm';
import { StateForm } from '@/components/FormFields/StateForm';
import { SupportSelectForm } from '@/components/FormFields/SupportSelectForm';
import { VISIBILITY_OPTIONS } from '@/constants/visibility';
import { TBroadcastScheduleProgramBodySegmentToolkitLogotiposForm } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/types';
import { DrawnVideo } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/DrawnVideo/DrawnVideo';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { LogosDrawerForm } from '@/features/logos/components/LogosDrawerForm';
import { TLogo, TLogosForm } from '@/features/logos/types';
import { CataloguePicker } from '@/hooks/use-catalogue-picker';
import { ReturnDisclosureWithData } from '@/hooks/useDisclosureWithData';
import { I18nextTranslationKeys } from '@/i18n/i18next';

type TAdditionalButton = {
  label: string;
  onClick: () => void;
  icon?: TIcon;
  disabled?: boolean;
};

type TLogotiposFormProps = {
  form: UseFormReturn<TBroadcastScheduleProgramBodySegmentToolkitLogotiposForm>;
  logoPicker: CataloguePicker<TLogo>;
  selectedLogo: TLogo | null | undefined;
  isCreatingLogos: boolean;
  drawerLogo: ReturnDisclosureWithData<unknown>;
  handleClickAdd: () => void;
  handleClickExit: () => void;
  handleSubmit: (data: TBroadcastScheduleProgramBodySegmentToolkitLogotiposForm) => void;
  handleSubmitLogo: (data: TLogosForm) => Promise<TLogo>;
  renderAdditionalButtons: () => TAdditionalButton[];
  onClickTooltipDate: (name: 'start_datetime' | 'end_datetime') => void;
};

export function LogotiposForm({
  form,
  logoPicker,
  selectedLogo,
  isCreatingLogos,
  drawerLogo,
  handleClickAdd,
  handleClickExit,
  handleSubmit,
  handleSubmitLogo,
  renderAdditionalButtons,
  onClickTooltipDate,
}: TLogotiposFormProps) {
  const { logoData } = useBroadcastScheduleProgram();
  const { t } = useTranslation();
  const logoSelectProps = useCatalogueSelectProps(logoPicker);
  const isOptionsLoading = logoPicker.isValueResolving;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleClickExit();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleClickExit]);

  return (
    <>
      <form>
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between border-b-[1px] border-accent-disabled-10">
            <Typography
              fontSize={FontSizeEnum.Xl}
              color={FontColorEnum.Dark10}
              weight={FontWeightEnum.Medium}
            >
              {`${logoData ? t('Editar logotipo') : t('Asignar logotipo')}`}
            </Typography>

            <IconButton
              size={IconButtonSizeEnum.LG}
              color={IconButtonColorEnum.NONE_DEFAULT}
              onClick={handleClickExit}
            >
              <CloseIcon size={IconSizeEnum.LG} />
            </IconButton>
          </div>
          <div className="flex flex-1 gap-1">
            <div className="flex w-1/2 items-center justify-center p-4">
              {selectedLogo?.parameters?.main_logo_presigned_url && (
                <img
                  alt="logo"
                  className="h-full max-h-[200px] w-full max-w-[200px] border-0 object-contain"
                  src={selectedLogo.parameters.main_logo_presigned_url}
                />
              )}
            </div>
            <div className="flex w-1/2 flex-col gap-2">
              <DrawnVideo form={form} name="parameters.bbox" onClick={handleClickAdd} />
            </div>
          </div>

          <RowContainer>
            <SelectForm
              form={form}
              name="logo_id"
              required
              label={t('Logotipos')}
              {...logoSelectProps}
              isLoading={isOptionsLoading}
              additionalButtons={renderAdditionalButtons()}
            />
            <SelectForm
              form={form}
              name="parameters.visibility"
              required
              label={t('Visibilidad')}
              options={VISIBILITY_OPTIONS}
            />
          </RowContainer>
          <RowContainer>
            <InputDate
              form={form}
              name="start_datetime"
              label={t('Inicio')}
              seconds
              help
              tooltipHelp={t('Rellenar') as I18nextTranslationKeys}
              iconTooltip={ClockIcon}
              onClickTooltip={() => onClickTooltipDate('start_datetime')}
            />
            <InputDate
              form={form}
              name="end_datetime"
              label={t('Fin')}
              seconds
              help
              tooltipHelp={t('Rellenar') as I18nextTranslationKeys}
              iconTooltip={ClockIcon}
              onClickTooltip={() => onClickTooltipDate('end_datetime')}
            />
          </RowContainer>
          {/* <RowContainer>
          <InputNumber form={form} name="value" required label={t('Valoración')} />
          <InputNumber form={form} name="audience" required label={t('Audiencia')} />
          </RowContainer> */}
          <RowContainer>
            <SupportSelectForm
              form={form}
              name="parameters.support_id"
              label={t('Soporte')}
              defaultToNone={true}
            />
            <InputNumber
              form={form}
              name="entity_score"
              required
              label={t('Fiabilidad')}
              min={0}
              max={100}
            />
          </RowContainer>
          <StateForm
            className="!justify-start"
            form={form}
            name="review_state"
            activedName={t('Marcar como revisado')}
            desactivedName={t('No revisado')}
          />
        </div>
        <FooterFormActions
          form={form}
          handleCancel={handleClickExit}
          handleSubmit={handleSubmit}
          isLoading={isOptionsLoading}
        />
      </form>

      <LogosDrawerForm
        isOpen={drawerLogo.isOpen}
        isMutating={isCreatingLogos}
        onClose={drawerLogo.close}
        onSubmit={handleSubmitLogo}
      />
    </>
  );
}
